import React from 'react';
import { useLocalization } from '../../context/Localization/localization';
import { GMCapitalize } from '../Case';
import css from './DetailCards.module.scss';
import { GMTextArea } from '../TextField';
import { TextAreaProps } from '../TextField/TextArea/TextArea/TextArea.types';
import { KeyboardLayouts } from '../SimpleKeyboard/Layout';
import { GMDialogYesNo } from '../Dialog';
import { GMGenericIcon } from '../Icon';
import { GMGenericButton } from '../ButtonBase';

type BaseProps = {
    title: string;
    details: string;
    index: string | number;
    /** Callback function when delete is clicked. An icon will appear when defined. */
    onDelete?: () => void;
    /** Callback function when edit is clicked. An icon will appear when defined. */
    onEdit?: () => void;
    editable?: boolean;
} & Pick<EditableDetailsProps, 'ariaLabel' | 'title' | 'label' | 'name' | 'id' | 'placeholder' | 'onBlurCallback' | 'onChangeCallback' | 'error'>;

export type IDetailCardProps = BaseProps;

export const DetailCard: React.FC<IDetailCardProps> = React.memo((props) => {
    const Localization = useLocalization();
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);

    return (
        <>
            <div className={css.card}>
                <div className={css.countContainer}>
                    <div className={css.count}>
                        <span>{props.index}</span>
                    </div>
                </div>
                <div className={css.detailContainer}>
                    <div className={css.titleWrapper}>
                        <h5 className={css.title}>
                            <label htmlFor={props.id}>
                                <GMCapitalize>
                                    {props.title} {Localization.Details}
                                </GMCapitalize>
                            </label>
                        </h5>
                        {props.onDelete && (
                            <div className={css.editButtons}>
                                <GMGenericButton
                                    onClick={() => setShowConfirmDelete(true)}
                                    title={Localization.ClickToRemoveService}
                                    ariaLabel={Localization.ClickToRemoveService}
                                    id={Localization.ClickToRemoveService}
                                >
                                    <div className={css.delete}>
                                        <GMGenericIcon iconType={'Close'} />
                                    </div>
                                </GMGenericButton>
                            </div>
                        )}
                    </div>
                    {props.editable ? (
                        <EditableDetails {...props}>
                            {props.details}
                        </EditableDetails>
                    ) : (
                        <Details>{props.details}</Details>
                    )}
                </div>
            </div>
            <GMDialogYesNo
                ariaLabel={Localization.AreYouSure}
                title={Localization.AreYouSure}
                open={showConfirmDelete}
                onNo={() => setShowConfirmDelete(false)}
                onYes={() => {
                    setShowConfirmDelete(false);
                    props.onDelete && props.onDelete();
                }}
            />
        </>
    );
});

interface EditableDetailsProps extends TextAreaProps {
    children: string;
}

const EditableDetails = (props: EditableDetailsProps) => (
    <GMTextArea
        name={props.name}
        id={props.id}
        title={props.title}
        label={''}
        ariaLabel={props.ariaLabel}
        inputValue={props.children}
        placeholder={props.placeholder}
        onChangeCallback={props.onChangeCallback}
        onBlurCallback={props.onBlurCallback}
        error={props.error}
        keyboardLayout={KeyboardLayouts.Alphanumeric}
        fullWidth
    />
);

const Details = (props: { children: string; }) => <div className={css.descriptionWrapper}>{props.children}</div>;
